import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Table, Button, Modal, Form, Pagination, Badge, Row, Col, InputGroup, Card, Spinner } from "react-bootstrap";
import { ToastContainer, toast } from 'react-toastify';
import * as XLSX from "xlsx";
import 'react-toastify/dist/ReactToastify.css';

// Razorpay configuration
const RAZORPAY_KEY_ID = 'rzp_live_R7WEc7UNXkN075';

// ✅ Polling interval — 5 seconds
const POLLING_INTERVAL_MS = 5000;

// Load Razorpay script dynamically
const loadRazorpayScript = (src) => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      console.log('Razorpay SDK already loaded');
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.onload = () => {
      console.log('Razorpay SDK loaded successfully');
      resolve(true);
    };
    script.onerror = (error) => {
      console.error('Failed to load Razorpay SDK:', error);
      resolve(false);
    };
    document.body.appendChild(script);
  });
};

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [showExtensionsModal, setShowExtensionsModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterField, setFilterField] = useState("name");
  const [searchQuery, setSearchQuery] = useState("");
  const [replacedCarDetails, setReplacedCarDetails] = useState({ oldCar: null, newCar: null });
  const [extensionData, setExtensionData] = useState({
    extendDeliveryDate: "",
    extendDeliveryTime: "",
    hours: "",
    amount: ""
  });
  const [replaceData, setReplaceData] = useState({
    newCarId: "",
    transactionId: "",
    amount: "",
    staffRefund: "",
    paymentType: "none"
  });
  const [cars, setCars] = useState([]);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isReplacingCar, setIsReplacingCar] = useState(false);

  // ============ NEW BOOKING POPUP STATE ============
  const [showNewBookingModal, setShowNewBookingModal] = useState(false);
  const [newBookingData, setNewBookingData] = useState(null);
  const [newBookingCount, setNewBookingCount] = useState(0);
  // ================================================

  // ============ REFS FOR DETECTION & SOUND ============
  const lastBookingIdsRef = useRef(new Set());
  const isInitialLoadRef = useRef(true);
  const notificationSoundRef = useRef(null);
  const notificationCheckInProgressRef = useRef(false);
  const pollingIntervalRef = useRef(null);
  const pollingPageRef = useRef(1);
  // ====================================================

  // Pagination state from backend
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalBookings: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Date Filters
  const [rentalStartDateFilter, setRentalStartDateFilter] = useState("");
  const [rentalEndDateFilter, setRentalEndDateFilter] = useState("");
  const [createdDateFilter, setCreatedDateFilter] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const bookingsPerPage = 10;

  // ============ NOTIFICATION SOUND SETUP ============
  const unlockNotificationAudio = () => {
    try {
      if (!notificationSoundRef.current) {
        notificationSoundRef.current = new Audio('/sounds/new-booking.mp3');
        notificationSoundRef.current.volume = 0.8;
        notificationSoundRef.current.load();
      }
      if (notificationSoundRef.current && typeof notificationSoundRef.current.play === 'function') {
        notificationSoundRef.current.play().catch(() => {});
      }
    } catch (err) {
      console.warn('Could not initialize notification sound:', err);
    }
  };

  useEffect(() => {
    const unlockAudio = () => {
      unlockNotificationAudio();
    };

    document.addEventListener('pointerdown', unlockAudio, { once: true });
    document.addEventListener('keydown', unlockAudio, { once: true });
    document.addEventListener('click', unlockAudio, { once: true });

    return () => {
      document.removeEventListener('pointerdown', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
      document.removeEventListener('click', unlockAudio);

      if (notificationSoundRef.current) {
        try { notificationSoundRef.current.pause(); } catch (e) { /* ignore */ }
        notificationSoundRef.current = null;
      }
    };
  }, []);
  // ===================================================

  // ============ BROWSER NOTIFICATION PERMISSION ============
  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission === 'default') {
        const timer = setTimeout(() => {
          Notification.requestPermission().catch(() => {});
        }, 5000);
        return () => clearTimeout(timer);
      }
    }
  }, []);
  // =========================================================

  // ============ PLAY SOUND & BROWSER NOTIFICATION ============
  const playNotificationSound = async () => {
    try {
      if (!notificationSoundRef.current) {
        notificationSoundRef.current = new Audio('/sounds/new-booking.mp3');
        notificationSoundRef.current.volume = 0.8;
        notificationSoundRef.current.load();
      }

      notificationSoundRef.current.currentTime = 0;
      await notificationSoundRef.current.play().catch((error) => {
        console.warn('Notification sound could not be played:', error);
      });
    } catch (error) {
      console.warn('Notification sound could not be played:', error);
    }
  };

  const showBrowserNotification = (booking) => {
    try {
      if (typeof window === 'undefined' || !('Notification' in window)) return;
      if (Notification.permission !== 'granted') return;

      const customerName = booking?.userId?.name || 'Customer';
      const notification = new Notification('New Car Booking', {
        body: `New booking received from ${customerName}`,
        icon: '/favicon.ico',
        tag: `booking-${booking?._id}`,
        requireInteraction: false,
      });

      setTimeout(() => {
        try { notification.close(); } catch (e) { /* ignore */ }
      }, 10000);
    } catch (error) {
      console.warn('Browser notification failed:', error);
    }
  };
  // ===========================================================

  // ============ HELPER: CHECK FOR NEW BOOKINGS ============
  const detectNewBookings = (newBookingsList) => {
    if (!Array.isArray(newBookingsList) || newBookingsList.length === 0) {
      console.log('[NEW BOOKING] No new booking detected');
      return;
    }

    const bookingIds = newBookingsList
      .map((booking) => booking?._id)
      .filter(Boolean);

    console.log('[NEW BOOKING] API returned:', newBookingsList);
    console.log('[NEW BOOKING] Existing IDs:', [...lastBookingIdsRef.current]);

    if (isInitialLoadRef.current) {
      console.log('[NEW BOOKING] Initial load - storing existing booking IDs:', bookingIds);
      bookingIds.forEach((id) => lastBookingIdsRef.current.add(id));
      isInitialLoadRef.current = false;
      return;
    }

    const freshBookings = newBookingsList.filter((booking) => {
      const id = booking?._id;
      return !!id && !lastBookingIdsRef.current.has(id);
    });

    console.log('[NEW BOOKING] Fresh bookings:', freshBookings);

    if (freshBookings.length === 0) {
      console.log('[NEW BOOKING] No new booking detected');
      return;
    }

    freshBookings.forEach((booking) => {
      if (booking?._id) lastBookingIdsRef.current.add(booking._id);
    });

    const newest = [...freshBookings].sort((a, b) => {
      const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    })[0];

    const toastMessage = freshBookings.length === 1
      ? '🔔 New Car Booking Received!'
      : `🔔 ${freshBookings.length} New Car Bookings Received!`;

    toast.success(toastMessage, {
      autoClose: 6000,
      toastId: `new-booking-${Date.now()}`,
    });

    console.log('[NEW BOOKING] Notification triggered:', newest?._id || freshBookings[0]?._id);

    playNotificationSound();
    showBrowserNotification(newest || freshBookings[0]);
    setNewBookingData(newest || freshBookings[0]);
    setNewBookingCount((prev) => prev + freshBookings.length);
    setShowNewBookingModal(true);

    if (!searchQuery.trim() && !rentalStartDateFilter && !rentalEndDateFilter && !createdDateFilter && currentPage === 1) {
      fetchBookings(1, null, null, true, true);
    }
  };
  // ======================================================

  // Fetch bookings with pagination and filters
  const fetchBookings = async (page = 1, searchBy = null, searchValue = null, silent = false, skipDetection = true) => {
    try {
      if (!silent) setIsLoading(true);

      let url = `https://varahibackend.varahiselfdrivecars.com/api/staff/allbookingsforadmin?page=${page}&limit=${bookingsPerPage}`;

      if (searchBy && searchValue) {
        url += `&searchBy=${searchBy}&searchValue=${searchValue}`;
      }

      const response = await axios.get(url);

      if (response.data?.bookings) {
        setBookings(response.data.bookings);
        setFilteredBookings(response.data.bookings);
        setPagination(response.data.pagination);
        setCurrentPage(response.data.pagination.currentPage);

        if (!skipDetection) {
          detectNewBookings(response.data.bookings);
        }
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
      if (!silent) toast.error("Failed to fetch bookings.");
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  // ============ FETCH ALL BOOKINGS (NO PAGINATION) FOR EXPORT ============
  const fetchAllBookingsForExport = async () => {
    try {
      let url = `https://varahibackend.varahiselfdrivecars.com/api/staff/allbookingsforadmin`;

      // Apply same filters as the UI (search + dates) but without page/limit
      const params = [];
      if (searchQuery.trim()) {
        params.push(`searchBy=${filterField}&searchValue=${encodeURIComponent(searchQuery)}`);
      }
      if (rentalStartDateFilter) params.push(`rentalstartdate=${rentalStartDateFilter}`);
      if (rentalEndDateFilter) params.push(`rentalenddate=${rentalEndDateFilter}`);
      if (createdDateFilter) params.push(`createdat=${createdDateFilter}`);

      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }

      const response = await axios.get(url);
      return response.data?.bookings || [];
    } catch (error) {
      console.error("Error fetching all bookings for export:", error);
      throw error;
    }
  };
  // ======================================================================

  // ============ NOTIFICATION-ONLY FETCH (independent of current page) ============
  const fetchLatestForNotification = async () => {
    if (notificationCheckInProgressRef.current) {
      console.log('[NEW BOOKING] Notification check skipped because previous one is still running');
      return;
    }

    notificationCheckInProgressRef.current = true;
    console.log('[NEW BOOKING] Polling started');

    try {
      const url = 'https://varahibackend.varahiselfdrivecars.com/api/staff/allbookingsforadmin?page=1&limit=10';
      const response = await axios.get(url);
      const bookings = response.data?.bookings || [];

      console.log('[NEW BOOKING] API returned:', bookings);
      detectNewBookings(bookings);
    } catch (error) {
      console.error('[NEW BOOKING] API error:', error);
      console.warn('Polling check failed:', error?.message);
    } finally {
      notificationCheckInProgressRef.current = false;
    }
  };
  // ==============================================================================

  // Fetch cars
  const fetchCars = async () => {
    try {
      const response = await axios.get("https://varahibackend.varahiselfdrivecars.com/api/car/get-cars");
      if (response.data?.cars) {
        setCars(response.data.cars);
      }
    } catch (error) {
      console.error("Error fetching cars:", error);
      toast.error("Failed to fetch available cars.");
    }
  };

  const fetchCarDetails = async (carId) => {
    if (!carId) return null;
    try {
      const response = await axios.get(`https://varahibackend.varahiselfdrivecars.com/api/car/getcar/${carId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching car details for ${carId}:`, error);
      return null;
    }
  };

  const fetchBookingDetails = async (bookingId) => {
    try {
      const existingBooking = bookings.find(b => b._id === bookingId);
      const response = await axios.get(`https://varahibackend.varahiselfdrivecars.com/api/staff/singlebooking/${bookingId}`);

      if (response.data?.booking) {
        const combinedBooking = {
          ...response.data.booking,
          delayedPaymentProof: existingBooking?.delayedPaymentProof
        };

        setBookingDetails(combinedBooking);

        if (combinedBooking.carReplacementHistory && combinedBooking.carReplacementHistory.length > 0) {
          const latestReplacement = combinedBooking.carReplacementHistory[combinedBooking.carReplacementHistory.length - 1];
          const oldCarId = latestReplacement.oldCarId?._id;
          const newCarId = latestReplacement.newCarId?._id;

          const [oldCarDetails, newCarDetails] = await Promise.all([
            oldCarId ? fetchCarDetails(oldCarId) : null,
            newCarId ? fetchCarDetails(newCarId) : null
          ]);

          setReplacedCarDetails({
            oldCar: oldCarDetails || latestReplacement.oldCarId,
            newCar: newCarDetails || latestReplacement.newCarId
          });
        } else {
          setReplacedCarDetails({ oldCar: null, newCar: null });
        }

        setShowDetailsModal(true);
      }
    } catch (error) {
      console.error("Error fetching booking details:", error);
      toast.error("Failed to fetch booking details.");
    }
  };

  const handleSearch = useCallback(async () => {
    if (searchQuery.trim()) {
      await fetchBookings(1, filterField, searchQuery);
    } else {
      await fetchBookings(1);
    }
  }, [searchQuery, filterField]);

  const filterByDates = useCallback(async () => {
    if (rentalStartDateFilter || rentalEndDateFilter || createdDateFilter) {
      setIsLoading(true);
      try {
        let url = `https://varahibackend.varahiselfdrivecars.com/api/staff/allbookingsforadmin?page=1&limit=${bookingsPerPage}`;
        if (rentalStartDateFilter) url += `&rentalstartdate=${rentalStartDateFilter}`;
        if (rentalEndDateFilter) url += `&rentalenddate=${rentalEndDateFilter}`;
        if (createdDateFilter) url += `&createdat=${createdDateFilter}`;

        const response = await axios.get(url);

        if (response.data?.bookings) {
          setBookings(response.data.bookings);
          setFilteredBookings(response.data.bookings);
          setPagination(response.data.pagination);
          setCurrentPage(1);
        }
      } catch (error) {
        console.error("Error filtering by dates:", error);
        toast.error("Failed to filter bookings by date.");
      } finally {
        setIsLoading(false);
      }
    } else {
      await fetchBookings(1);
    }
  }, [rentalStartDateFilter, rentalEndDateFilter, createdDateFilter]);

  useEffect(() => {
    fetchBookings();
    fetchCars();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    pollingPageRef.current = currentPage;
  }, [currentPage]);

  // ============ AUTO-POLLING FOR NEW BOOKINGS (every 5 sec) ============
  useEffect(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(() => {
      fetchLatestForNotification();

      if (pollingPageRef.current === 1 && !searchQuery.trim() && !rentalStartDateFilter && !rentalEndDateFilter && !createdDateFilter) {
        fetchBookings(1, null, null, true, true);
      }
    }, POLLING_INTERVAL_MS);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, rentalStartDateFilter, rentalEndDateFilter, createdDateFilter]);
  // ======================================================================

  useEffect(() => {
    if (!searchQuery.trim()) {
      filterByDates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rentalStartDateFilter, rentalEndDateFilter, createdDateFilter]);

  const handleEdit = (booking) => {
    setSelectedBooking({ ...booking, editedAmount: booking.totalPrice });
    setShowEditModal(true);
  };

  const handleExtend = (booking) => {
    setSelectedBooking(booking);
    setExtensionData({ extendDeliveryDate: "", extendDeliveryTime: "", hours: "", amount: "" });
    setShowExtendModal(true);
  };

  const handleViewExtensions = (booking) => {
    setSelectedBooking(booking);
    setShowExtensionsModal(true);
  };

  const handleReplace = (booking) => {
    setSelectedBooking(booking);
    setReplaceData({ newCarId: "", transactionId: "", amount: "", staffRefund: "", paymentType: "none" });
    setShowReplaceModal(true);
  };

  const handleGenerateOTP = async (bookingId) => {
    try {
      toast.info("Generating OTP...", { autoClose: false, toastId: 'otp-loading' });
      const response = await axios.put(`https://varahibackend.varahiselfdrivecars.com/api/staff/update-otp/${bookingId}`);
      toast.dismiss('otp-loading');

      if (response.data && response.data.otp) {
        toast.success(`OTP generated successfully: ${response.data.otp}`, { autoClose: 5000 });
        await fetchBookings(currentPage);
      } else {
        toast.success("OTP generated successfully!");
        await fetchBookings(currentPage);
      }
    } catch (error) {
      toast.dismiss('otp-loading');
      console.error("Error generating OTP:", error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || "Failed to generate OTP";
      toast.error(`Error: ${errorMessage}`);
    }
  };

  const initRazorpayPayment = async () => {
    try {
      if (!selectedBooking) { toast.error("No booking selected"); return; }
      const userId = selectedBooking.userId?._id;
      const bookingId = selectedBooking._id;
      if (!userId) { toast.error("User ID not found in booking data"); return; }
      if (!extensionData.amount || parseFloat(extensionData.amount) <= 0) { toast.error("Please enter a valid extension amount"); return; }
      if (!extensionData.hours && (!extensionData.extendDeliveryDate || !extensionData.extendDeliveryTime)) {
        toast.error("Please provide either hours or date/time for extension"); return;
      }

      setIsProcessingPayment(true);
      toast.info("Initializing payment gateway...", { autoClose: 1500 });

      const isScriptLoaded = await loadRazorpayScript('https://checkout.razorpay.com/v1/checkout.js');
      if (!isScriptLoaded) { toast.error("Payment gateway failed to load."); setIsProcessingPayment(false); return; }

      await new Promise(resolve => setTimeout(resolve, 500));
      if (!window.Razorpay) { toast.error("Payment service unavailable."); setIsProcessingPayment(false); return; }

      const amountInPaise = Math.round(parseFloat(extensionData.amount) * 100);
      const currentDate = new Date().toLocaleDateString('en-IN');

      const options = {
        key: RAZORPAY_KEY_ID,
        amount: amountInPaise,
        currency: "INR",
        name: "Car Rental Extension",
        description: `Booking Extension - ${bookingId.slice(-6)}`,
        handler: async (response) => { await handleExtensionPaymentSuccess(response.razorpay_payment_id); },
        prefill: {
          name: selectedBooking.userId?.name || "",
          email: selectedBooking.userId?.email || "",
          contact: selectedBooking.userId?.mobile || ""
        },
        notes: {
          bookingId, userId, extensionDate: currentDate,
          extensionType: extensionData.hours ? 'hours' : 'datetime',
          value: extensionData.hours || `${extensionData.extendDeliveryDate} ${extensionData.extendDeliveryTime}`
        },
        theme: { color: "#3399cc" },
        modal: {
          ondismiss: function () {
            if (!isProcessingPayment) toast.info("Payment was cancelled");
            setIsProcessingPayment(false);
          }
        }
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.on('payment.failed', function (response) {
        const errorDescription = response.error?.description || 'Payment failed';
        toast.error(`Payment Failed: ${errorDescription}`);
        setIsProcessingPayment(false);
      });
      razorpayInstance.open();
    } catch (error) {
      console.error("Error initializing Razorpay:", error);
      toast.error("Failed to initialize payment.");
      setIsProcessingPayment(false);
    }
  };

  const handleExtensionPaymentSuccess = async (paymentId) => {
    try {
      if (!selectedBooking) return;
      const userId = selectedBooking.userId?._id;
      const bookingId = selectedBooking._id;
      if (!userId) { toast.error("User ID not found"); setIsProcessingPayment(false); return; }

      const response = await axios.put(
        `https://varahibackend.varahiselfdrivecars.com/api/users/extendbookings/${userId}/${bookingId}`,
        { ...extensionData, transactionId: paymentId }
      );

      if (response.data.message) {
        toast.success(response.data.message);
        setShowExtendModal(false);
        await fetchBookings(currentPage);
        setExtensionData({ extendDeliveryDate: "", extendDeliveryTime: "", hours: "", amount: "" });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to extend booking";
      toast.error(`Error: ${errorMessage}`);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleReplaceCar = async () => {
    try {
      if (!selectedBooking) { toast.error("No booking selected"); return; }
      const userId = selectedBooking.userId?._id;
      const bookingId = selectedBooking._id;
      if (!userId) { toast.error("User ID not found"); return; }
      if (!replaceData.newCarId) { toast.error("Please select a new car"); return; }

      setIsReplacingCar(true);
      let payload = { bookingId, newCarId: replaceData.newCarId };

      if (replaceData.staffRefund) {
        payload.staffRefund = parseFloat(replaceData.staffRefund);
        if (replaceData.amount && replaceData.transactionId) {
          payload.amount = parseFloat(replaceData.amount);
          payload.transactionId = replaceData.transactionId;
        }
      } else {
        if (replaceData.requirePayment && replaceData.transactionId && replaceData.amount) {
          payload.transactionId = replaceData.transactionId;
          payload.amount = parseFloat(replaceData.amount);
        }
      }

      const response = await axios.put(
        `https://varahibackend.varahiselfdrivecars.com/api/users/replace-car/${userId}`,
        payload
      );

      if (response.data.message) {
        toast.success(response.data.message);
        setShowReplaceModal(false);
        await fetchBookings(currentPage);
        setReplaceData({ newCarId: "", transactionId: "", amount: "", staffRefund: "", requirePayment: false });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to replace car";
      toast.error(`Error: ${errorMessage}`);
    } finally {
      setIsReplacingCar(false);
    }
  };

  const initReplaceCarPayment = async () => {
    try {
      if (!selectedBooking) { toast.error("No booking selected"); return; }
      if (!replaceData.amount || parseFloat(replaceData.amount) <= 0) { toast.error("Enter a valid amount"); return; }

      setIsProcessingPayment(true);
      toast.info("Initializing payment gateway...", { autoClose: 1500 });

      const isScriptLoaded = await loadRazorpayScript('https://checkout.razorpay.com/v1/checkout.js');
      if (!isScriptLoaded) { toast.error("Payment gateway failed to load."); setIsProcessingPayment(false); return; }

      await new Promise(resolve => setTimeout(resolve, 500));
      if (!window.Razorpay) { toast.error("Payment service unavailable."); setIsProcessingPayment(false); return; }

      const bookingId = selectedBooking._id;
      const amountInPaise = Math.round(parseFloat(replaceData.amount) * 100);

      const options = {
        key: RAZORPAY_KEY_ID,
        amount: amountInPaise,
        currency: "INR",
        name: "Car Replacement Payment",
        description: `Car Replacement - Booking ${bookingId.slice(-6)}`,
        handler: async (response) => {
          setReplaceData(prev => ({ ...prev, transactionId: response.razorpay_payment_id }));
          await new Promise(resolve => setTimeout(resolve, 100));
          await handleReplaceCar();
        },
        prefill: {
          name: selectedBooking.userId?.name || "",
          email: selectedBooking.userId?.email || "",
          contact: selectedBooking.userId?.mobile || ""
        },
        notes: { bookingId, purpose: "car_replacement" },
        theme: { color: "#3399cc" },
        modal: {
          ondismiss: function () {
            toast.info("Payment was cancelled");
            setIsProcessingPayment(false);
          }
        }
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.on('payment.failed', function (response) {
        const errorDescription = response.error?.description || 'Payment failed';
        toast.error(`Payment Failed: ${errorDescription}`);
        setIsProcessingPayment(false);
      });
      razorpayInstance.open();
    } catch (error) {
      console.error("Error initializing Razorpay:", error);
      toast.error("Failed to initialize payment.");
      setIsProcessingPayment(false);
    }
  };

  const handleViewDetails = (bookingId) => {
    fetchBookingDetails(bookingId);
  };

  const handleSaveChanges = async () => {
    try {
      const { _id, status, paymentStatus, editedAmount } = selectedBooking;
      await axios.put(`https://varahibackend.varahiselfdrivecars.com/api/admin/statusbookings/${_id}`, { status });
      await axios.put(`https://varahibackend.varahiselfdrivecars.com/api/admin/payment-status/${_id}`, { paymentStatus, amount: editedAmount });
      await fetchBookings(currentPage);
      setShowEditModal(false);
      toast.success("Booking updated successfully!");
    } catch (error) {
      console.error("Error updating booking:", error);
      toast.error("Failed to update booking.");
    }
  };

  const handleDelete = async (bookingId) => {
    if (window.confirm("Are you sure you want to delete this booking?")) {
      try {
        await axios.delete(`https://varahibackend.varahiselfdrivecars.com/api/admin/deletebooking/${bookingId}`);
        await fetchBookings(currentPage);
        toast.success("Booking deleted successfully!");
      } catch (error) {
        console.error("Error deleting booking:", error);
        toast.error("Failed to delete booking.");
      }
    }
  };

  const getStatusBadge = (status) => {
    if (!status) return 'secondary';
    switch (status.toLowerCase()) {
      case 'pending': return 'warning';
      case 'confirmed': return 'info';
      case 'active': return 'primary';
      case 'completed': return 'success';
      case 'cancelled': return 'danger';
      default: return 'secondary';
    }
  };

  const getPaymentBadge = (paymentStatus) => {
    if (!paymentStatus) return 'secondary';
    switch (paymentStatus.toLowerCase()) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'partial': return 'info';
      default: return 'secondary';
    }
  };

  const getReplacementBadge = (booking) => {
    const hasReplacement = booking?.carReplacementHistory && booking.carReplacementHistory.length > 0;
    if (hasReplacement) return <Badge bg="warning" className="ms-1">Car Replaced</Badge>;
    return null;
  };

  const getReplacementStatus = (booking) => {
    const hasReplacement = booking?.carReplacementHistory && booking.carReplacementHistory.length > 0;
    if (hasReplacement) return <Badge bg="warning" className="text-capitalize">Yes</Badge>;
    return <Badge bg="secondary" className="text-capitalize">No</Badge>;
  };

  const renderPagination = () => {
    if (!pagination.totalPages || pagination.totalPages < 1) return null;
    const pages = [];
    const pageSet = new Set();
    pageSet.add(1);
    if (pagination.totalPages > 1) pageSet.add(pagination.totalPages);
    if (pagination.currentPage > 1) pageSet.add(pagination.currentPage - 1);
    pageSet.add(pagination.currentPage);
    if (pagination.currentPage < pagination.totalPages) pageSet.add(pagination.currentPage + 1);

    const sortedPages = Array.from(pageSet).sort((a, b) => a - b);
    let lastPage = 0;
    sortedPages.forEach((page) => {
      if (page - lastPage > 1) pages.push(<Pagination.Ellipsis key={`ellipsis-${page}`} disabled />);
      pages.push(
        <Pagination.Item key={page} active={page === pagination.currentPage} onClick={() => handlePageChange(page)}>
          {page}
        </Pagination.Item>
      );
      lastPage = page;
    });

    return (
      <Pagination className="mt-3 justify-content-center">
        <Pagination.Item disabled={!pagination.hasPrevPage} onClick={() => pagination.hasPrevPage && handlePageChange(pagination.currentPage - 1)}>
          Prev
        </Pagination.Item>
        {pages}
        <Pagination.Item disabled={!pagination.hasNextPage} onClick={() => pagination.hasNextPage && handlePageChange(pagination.currentPage + 1)}>
          Next
        </Pagination.Item>
      </Pagination>
    );
  };

  const handlePageChange = (page) => {
    if (searchQuery.trim()) {
      fetchBookings(page, filterField, searchQuery);
    } else if (rentalStartDateFilter || rentalEndDateFilter || createdDateFilter) {
      let url = `https://varahibackend.varahiselfdrivecars.com/api/staff/allbookingsforadmin?page=${page}&limit=${bookingsPerPage}`;
      if (rentalStartDateFilter) url += `&rentalstartdate=${rentalStartDateFilter}`;
      if (rentalEndDateFilter) url += `&rentalenddate=${rentalEndDateFilter}`;
      if (createdDateFilter) url += `&createdat=${createdDateFilter}`;

      axios.get(url).then(response => {
        if (response.data?.bookings) {
          setBookings(response.data.bookings);
          setFilteredBookings(response.data.bookings);
          setPagination(response.data.pagination);
          setCurrentPage(page);
        }
      }).catch(error => {
        console.error("Error fetching bookings:", error); 
        toast.error("Failed to fetch bookings.");
      });
    } else {
      fetchBookings(page);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setIsDownloading(true);
      toast.info('Preparing full booking report...', { autoClose: 2000 });

      // Fetch ALL bookings (no pagination), applying current filters
      const allBookings = await fetchAllBookingsForExport();

      if (!allBookings || allBookings.length === 0) {
        toast.warning('No data to export!');
        setIsDownloading(false);
        return;
      }

      const dataToExport = allBookings.map(booking => ({
        'Booking ID': booking._id || '-',
        'User Name': booking.userId?.name || '-',
        'User Email': booking.userId?.email || '-',
        'User Mobile': booking.userId?.mobile || '-',
        'Car Name': booking.car?.carName || '-',
        'Car Model': booking.car?.model || '-',
        'Vehicle Number': booking.car?.vehicleNumber || '-',
        'Car Replacement': booking.carReplacementHistory?.length > 0 ? 'Yes' : 'No',
        'Extension Count': booking.extensions?.length || 0,
        'Rental Start Date': booking.rentalStartDate ? new Date(booking.rentalStartDate).toLocaleDateString() : '-',
        'Rental End Date': booking.rentalEndDate ? new Date(booking.rentalEndDate).toLocaleDateString() : '-',
        'Timings': `${booking.from || '-'} - ${booking.to || '-'}`,
        'Total Price': booking.totalPrice || 0,
        'Pickup Location': booking.pickupLocation || '-',
        'Status': booking.status || '-',
        'Payment Status': booking.paymentStatus || '-',
        'OTP': booking.otp || '-',
        'Return OTP': booking.returnOTP || '-',
        'Deposit Amount': booking.deposit || 0,
        'Booking Created Date': booking.createdAt ? new Date(booking.createdAt).toLocaleDateString() : '-',
        'Booking Created Time': booking.createdAt ? new Date(booking.createdAt).toLocaleTimeString() : '-',
      }));

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Bookings");

      const dateStr = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `bookings_${dateStr}.xlsx`);
      toast.success(`${dataToExport.length} bookings exported successfully!`, { autoClose: 2000 });
    } catch (error) {
      console.error('Error generating Excel report:', error);
      toast.error('Failed to generate Excel report: ' + error.message, { autoClose: 2000 });
    } finally {
      setIsDownloading(false);
    }
  };

  const renderCarInfo = (booking) => {
    if (booking.carReplacementHistory && booking.carReplacementHistory.length > 0) {
      return (
        <div>
          <strong>Current Car:</strong> {booking.car?.carName || 'N/A'}
          {getReplacementBadge(booking)}
        </div>
      );
    }
    return <div>{booking.car?.carName || 'N/A'}</div>;
  };

  const renderCarModel = (booking) => <div>{booking.car?.model || 'N/A'}</div>;

  const renderExtensions = (booking) => {
    const extensions = booking.extensions || [];
    if (extensions.length === 0) return <Badge bg="secondary">No Extensions</Badge>;
    return (
      <div>
        <Badge bg="info">{extensions.length} Extension{extensions.length > 1 ? 's' : ''}</Badge>
        <Button variant="link" size="sm" className="p-0 ms-2" onClick={() => handleViewExtensions(booking)}>
          View Details
        </Button>
      </div>
    );
  };

  const clearAllFilters = () => {
    setSearchQuery("");
    setFilterField("name");
    setRentalStartDateFilter("");
    setRentalEndDateFilter("");
    setCreatedDateFilter("");
    fetchBookings(1);
  };

  const handleViewNewBookingDetails = async () => {
    if (newBookingData?._id) {
      setShowNewBookingModal(false);
      await fetchBookingDetails(newBookingData._id);
    }
  };

  const handleCloseNewBookingModal = () => {
    setShowNewBookingModal(false);
  };

  const handleDismissNewBadge = () => {
    setNewBookingCount(0);
    setNewBookingData(null);
    setShowNewBookingModal(false);
  };

  return (
    <div className="container-fluid mt-4 px-3 px-md-4">
      <ToastContainer position="top-right" autoClose={2000} />

      {/* Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <h2 className="mb-0">Bookings Management</h2>
          {newBookingCount > 0 && (
            <Badge
              bg="danger"
              pill
              style={{ cursor: 'pointer', fontSize: '0.9rem' }}
              onClick={() => setShowNewBookingModal(true)}
              title="Click to view new booking(s)"
            >
              🔔 {newBookingCount} New
            </Badge>
          )}
        </div>
        <Badge bg="info" className="p-2 align-self-start align-self-md-center">
          Showing {filteredBookings.length} of {pagination.totalBookings} bookings | Page {pagination.currentPage} of {pagination.totalPages}
        </Badge>
      </div>

      {/* Filter Section */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Row className="g-3">
            <Col xs={12} md={3}>
              <Form.Label className="fw-semibold">Search Field</Form.Label>
              <Form.Select value={filterField} onChange={(e) => setFilterField(e.target.value)} className="w-100">
                <option value="id">Booking ID</option>
                <option value="name">Name</option>
                <option value="email">Email</option>
                <option value="pickuplocation">Pickup Location</option>
                <option value="status">Status</option>
                <option value="paymentstatus">Payment Status</option>
              </Form.Select>
            </Col>

            <Col xs={12} md={3}>
              <Form.Label className="fw-semibold">Search Value</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder={`Search by ${filterField}...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button variant="primary" onClick={handleSearch}>
                  <i className="fas fa-search me-1"></i> Search
                </Button>
              </InputGroup>
            </Col>

            <Col xs={12} md={2}>
              <Form.Label className="fw-semibold">Rental Start Date</Form.Label>
              <Form.Control type="date" value={rentalStartDateFilter} onChange={(e) => setRentalStartDateFilter(e.target.value)} />
            </Col>

            <Col xs={12} md={2}>
              <Form.Label className="fw-semibold">Rental End Date</Form.Label>
              <Form.Control type="date" value={rentalEndDateFilter} onChange={(e) => setRentalEndDateFilter(e.target.value)} />
            </Col>

            <Col xs={12} md={2}>
              <Form.Label className="fw-semibold">Created Date</Form.Label>
              <Form.Control type="date" value={createdDateFilter} onChange={(e) => setCreatedDateFilter(e.target.value)} />
            </Col>
          </Row>

          <Row className="mt-3">
            <Col xs={12} className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <Button variant="outline-secondary" size="sm" onClick={clearAllFilters}>
                <i className="fas fa-times me-2"></i>Clear All Filters
              </Button>
              <Button variant="success" size="sm" onClick={handleDownloadExcel} disabled={isDownloading || filteredBookings.length === 0}>
                {isDownloading ? (
                  <><Spinner as="span" animation="border" size="sm" className="me-2" />Exporting...</>
                ) : (
                  <><i className="fas fa-file-excel me-2"></i>Export to Excel</>
                )}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Table */}
      <Card className="shadow-sm">
        <Card.Body className="p-0">
          <div className="table-responsive" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            {isLoading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2">Loading bookings...</p>
              </div>
            ) : filteredBookings.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-muted">No bookings found matching your criteria.</p>
              </div>
            ) : (
              <Table bordered hover responsive className="mb-0">
                <thead style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#f8f9fa' }}>
                  <tr className="table-header">
                    <th style={{ minWidth: '60px' }}>S.NO</th>
                    <th style={{ minWidth: '100px' }}>Booking ID</th>
                    <th style={{ minWidth: '120px' }}>Name</th>
                    <th style={{ minWidth: '180px' }}>Email</th>
                    <th style={{ minWidth: '140px' }}>Car</th>
                    <th style={{ minWidth: '120px' }}>Model</th>
                    <th style={{ minWidth: '100px' }}>Replaced</th>
                    <th style={{ minWidth: '120px' }}>Extensions</th>
                    <th style={{ minWidth: '120px' }}>Rental Start Date</th>
                    <th style={{ minWidth: '120px' }}>Rental End Date</th>
                    <th style={{ minWidth: '120px' }}>Booking Created Date</th>
                    <th style={{ minWidth: '100px' }}>Timings</th>
                    <th style={{ minWidth: '100px' }}>Total Price</th>
                    <th style={{ minWidth: '150px' }}>Pickup Location</th>
                    <th style={{ minWidth: '100px' }}>Status</th>
                    <th style={{ minWidth: '100px' }}>Payment</th>
                    <th style={{ minWidth: '80px' }}>OTP</th>
                    <th style={{ minWidth: '80px' }}>Return OTP</th>
                    <th style={{ minWidth: '180px' }}>Actions</th>
                    <th style={{ minWidth: '80px' }}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((booking, index) => (
                    <tr key={booking._id} className={booking.carReplacementHistory?.length > 0 ? 'table-info' : ''}>
                      <td className="text-center">{((pagination.currentPage - 1) * bookingsPerPage) + index + 1}</td>
                      <td className="align-middle">{booking._id?.slice(-8) || 'N/A'}</td>
                      <td className="align-middle">{booking.userId?.name || 'N/A'}</td>
                      <td className="align-middle">{booking.userId?.email || 'N/A'}</td>
                      <td className="align-middle">{renderCarInfo(booking)}</td>
                      <td className="align-middle">{renderCarModel(booking)}</td>
                      <td className="align-middle">{getReplacementStatus(booking)}</td>
                      <td className="align-middle">{renderExtensions(booking)}</td>
                      <td className="align-middle">{booking.rentalStartDate ? new Date(booking.rentalStartDate).toLocaleDateString() : 'N/A'}</td>
                      <td className="align-middle">{booking.rentalEndDate ? new Date(booking.rentalEndDate).toLocaleDateString() : 'N/A'}</td>
                      <td className="align-middle">{booking.createdAt ? new Date(booking.createdAt).toLocaleDateString() : 'N/A'}</td>
                      <td className="align-middle">{booking.from || 'N/A'} - {booking.to || 'N/A'}</td>
                      <td className="align-middle">₹{booking.totalPrice || '0'}</td>
                      <td className="align-middle">{booking.pickupLocation || 'N/A'}</td>
                      <td className="align-middle">
                        <Badge bg={getStatusBadge(booking.status)} className="text-capitalize">{booking.status || 'N/A'}</Badge>
                      </td>
                      <td className="align-middle">
                        <Badge bg={getPaymentBadge(booking.paymentStatus)} className="text-capitalize">{booking.paymentStatus || 'N/A'}</Badge>
                      </td>
                      <td className="align-middle">
                        {booking.otp || 'N/A'}
                        {!booking.otp && (
                          <Button variant="outline-success" size="sm" className="ms-1" onClick={() => handleGenerateOTP(booking._id)} title="Generate OTP">
                            <i className="fas fa-key"></i>
                          </Button>
                        )}
                      </td>
                      <td className="align-middle">{booking.returnOTP || 'N/A'}</td>
                      <td className="align-middle">
                        <div className="d-flex flex-wrap gap-1">
                          <Button variant="outline-warning" size="sm" onClick={() => handleEdit(booking)} title="Edit"><i className="fas fa-edit"></i></Button>
                          <Button variant="outline-primary" size="sm" onClick={() => handleExtend(booking)} title="Extend"><i className="fas fa-clock"></i></Button>
                          <Button variant="outline-info" size="sm" onClick={() => handleReplace(booking)} title="Replace Car"><i className="fas fa-exchange-alt"></i></Button>
                          <Button variant="outline-danger" size="sm" onClick={() => handleDelete(booking._id)} title="Delete"><i className="fas fa-trash-alt"></i></Button>
                        </div>
                      </td>
                      <td className="align-middle">
                        <Button variant="outline-info" size="sm" onClick={() => handleViewDetails(booking._id)}>View</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>

          {!isLoading && filteredBookings.length > 0 && renderPagination()}

          {!isLoading && filteredBookings.length > 0 && (
            <div className="text-center text-muted mt-3 pb-3">
              <small>
                Showing {filteredBookings.length} of {pagination.totalBookings} bookings |
                Page {pagination.currentPage} of {pagination.totalPages}
              </small>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* NEW BOOKING POPUP MODAL */}
      <Modal
        show={showNewBookingModal}
        onHide={handleCloseNewBookingModal}
        size="lg"
        centered
        backdrop="static"
        dialogClassName="new-booking-modal"
      >
        <Modal.Header closeButton className="bg-success text-white">
          <Modal.Title className="d-flex align-items-center">
            <i className="fas fa-bell me-2"></i>
            🔔 New Booking Received!
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {newBookingData ? (
            <>
              {newBookingCount > 1 && (
                <div className="alert alert-info mb-3">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>{newBookingCount} new bookings</strong> received. Showing the latest one below.
                </div>
              )}

              <div className="row">
                <div className="col-md-6">
                  <h6 className="text-primary border-bottom pb-2">Customer Details</h6>
                  <p><strong>Name:</strong> {newBookingData.userId?.name || 'N/A'}</p>
                  <p><strong>Email:</strong> {newBookingData.userId?.email || 'N/A'}</p>
                  <p><strong>Mobile:</strong> {newBookingData.userId?.mobile || 'N/A'}</p>
                </div>
                <div className="col-md-6">
                  <h6 className="text-primary border-bottom pb-2">Booking Details</h6>
                  <p><strong>Booking ID:</strong> <code>{newBookingData._id?.slice(-8)}</code></p>
                  <p><strong>Booking Time:</strong> {newBookingData.createdAt ? new Date(newBookingData.createdAt).toLocaleString() : 'N/A'}</p>
                  <p><strong>Status:</strong> <Badge bg={getStatusBadge(newBookingData.status)}>{newBookingData.status || 'N/A'}</Badge></p>
                  <p><strong>Payment:</strong> <Badge bg={getPaymentBadge(newBookingData.paymentStatus)}>{newBookingData.paymentStatus || 'N/A'}</Badge></p>
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-md-6">
                  <h6 className="text-primary border-bottom pb-2">Car Details</h6>
                  <p><strong>Car:</strong> {newBookingData.car?.carName || 'N/A'}</p>
                  <p><strong>Model:</strong> {newBookingData.car?.model || 'N/A'}</p>
                  <p><strong>Vehicle Number:</strong> {newBookingData.car?.vehicleNumber || 'N/A'}</p>
                </div>
                <div className="col-md-6">
                  <h6 className="text-primary border-bottom pb-2">Rental Info</h6>
                  <p><strong>Start Date:</strong> {newBookingData.rentalStartDate ? new Date(newBookingData.rentalStartDate).toLocaleDateString() : 'N/A'}</p>
                  <p><strong>End Date:</strong> {newBookingData.rentalEndDate ? new Date(newBookingData.rentalEndDate).toLocaleDateString() : 'N/A'}</p>
                  <p><strong>Pickup Location:</strong> {newBookingData.pickupLocation || 'N/A'}</p>
                  <p><strong>Total Price:</strong> <span className="text-success fw-bold">₹{newBookingData.totalPrice || 0}</span></p>
                  <p><strong>Deposit:</strong> ₹{newBookingData.deposit || 0}</p>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-3"><Spinner animation="border" /></div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button variant="outline-secondary" onClick={handleDismissNewBadge}>Dismiss Badge</Button>
          <div>
            <Button variant="secondary" className="me-2" onClick={handleCloseNewBookingModal}>Close</Button>
            <Button variant="primary" onClick={handleViewNewBookingDetails}>
              <i className="fas fa-eye me-2"></i>View Full Details
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton><Modal.Title>Edit Booking</Modal.Title></Modal.Header>
        <Modal.Body>
          {selectedBooking && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select value={selectedBooking.status} onChange={(e) => setSelectedBooking({ ...selectedBooking, status: e.target.value })}>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Payment Status</Form.Label>
                <Form.Select value={selectedBooking.paymentStatus} onChange={(e) => setSelectedBooking({ ...selectedBooking, paymentStatus: e.target.value })}>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="partial">Partial</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Payment Amount (₹)</Form.Label>
                <Form.Control type="number" value={selectedBooking.editedAmount} onChange={(e) => setSelectedBooking({ ...selectedBooking, editedAmount: e.target.value })} />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveChanges}>Save Changes</Button>
        </Modal.Footer>
      </Modal>

      {/* Extend Booking Modal */}
      <Modal show={showExtendModal} onHide={() => setShowExtendModal(false)} size="lg">
        <Modal.Header closeButton><Modal.Title>Extend Booking</Modal.Title></Modal.Header>
        <Modal.Body>
          {selectedBooking && (
            <Form>
              <div className="mb-3">
                <p><strong>Booking ID:</strong> {selectedBooking._id}</p>
                <p><strong>User:</strong> {selectedBooking.userId?.name} ({selectedBooking.userId?.email})</p>
                <p><strong>Current End:</strong> {new Date(selectedBooking.rentalEndDate).toLocaleDateString()} {selectedBooking.to}</p>
              </div>
              <Form.Group className="mb-3">
                <Form.Label>Extension Hours</Form.Label>
                <Form.Control type="number" placeholder="Enter hours (optional)" value={extensionData.hours} onChange={(e) => setExtensionData({ ...extensionData, hours: e.target.value })} min="1" step="1" />
              </Form.Group>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Extended Delivery Date</Form.Label>
                    <Form.Control type="date" value={extensionData.extendDeliveryDate} onChange={(e) => setExtensionData({ ...extensionData, extendDeliveryDate: e.target.value })} min={selectedBooking.rentalEndDate} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Extended Delivery Time</Form.Label>
                    <Form.Control type="time" value={extensionData.extendDeliveryTime} onChange={(e) => setExtensionData({ ...extensionData, extendDeliveryTime: e.target.value })} />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>Extension Amount (₹)</Form.Label>
                <Form.Control type="number" placeholder="Enter amount" value={extensionData.amount} onChange={(e) => setExtensionData({ ...extensionData, amount: e.target.value })} required min="1" step="0.01" />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowExtendModal(false)} disabled={isProcessingPayment}>Cancel</Button>
          <Button variant="success" onClick={initRazorpayPayment} disabled={isProcessingPayment || !extensionData.amount}>
            {isProcessingPayment ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Processing...</> : <><i className="fas fa-credit-card me-2"></i>Pay & Extend</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Replace Car Modal */}
      <Modal show={showReplaceModal} onHide={() => setShowReplaceModal(false)} size="lg">
        <Modal.Header closeButton><Modal.Title>Replace Car in Booking</Modal.Title></Modal.Header>
        <Modal.Body>
          {selectedBooking && (
            <Form>
              <div className="mb-3">
                <p><strong>Booking ID:</strong> {selectedBooking._id}</p>
                <p><strong>User:</strong> {selectedBooking.userId?.name}</p>
                <p><strong>Current Car:</strong> {selectedBooking.car?.carName}</p>
              </div>
              <Form.Group className="mb-3">
                <Form.Label>Select New Car</Form.Label>
                <Form.Select value={replaceData.newCarId} onChange={(e) => setReplaceData({ ...replaceData, newCarId: e.target.value })} required>
                  <option value="">Select a car</option>
                  {cars.map((car) => (
                    <option key={car._id} value={car._id}>{car.carName} - {car.model} - {car.vehicleNumber}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Staff Refund Amount (Optional)</Form.Label>
                <Form.Control type="number" placeholder="Enter staff refund" value={replaceData.staffRefund} onChange={(e) => setReplaceData({ ...replaceData, staffRefund: e.target.value })} min="0" />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReplaceModal(false)} disabled={isReplacingCar}>Cancel</Button>
          <Button variant="warning" onClick={handleReplaceCar} disabled={isReplacingCar || !replaceData.newCarId}>
            {isReplacingCar ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Replacing...</> : <><i className="fas fa-exchange-alt me-2"></i>Replace Car</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Extensions Modal */}
      <Modal show={showExtensionsModal} onHide={() => setShowExtensionsModal(false)} size="xl" centered>
        <Modal.Header closeButton><Modal.Title><i className="fas fa-history me-2"></i>Booking Extensions</Modal.Title></Modal.Header>
        <Modal.Body>
          {selectedBooking && (
            <>
              <div className="card border-0 bg-light mb-4">
                <div className="card-body">
                  <h5 className="mb-3">Booking Information</h5>
                  <div className="row">
                    <div className="col-md-6">
                      <p><strong>Booking ID:</strong> {selectedBooking._id}</p>
                      <p><strong>User:</strong> {selectedBooking.userId?.name}</p>
                    </div>
                    <div className="col-md-6">
                      <p><strong>Car:</strong> {selectedBooking.car?.carName}</p>
                      <p><strong>Current Rental End:</strong> {new Date(selectedBooking.rentalEndDate).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              </div>
              {selectedBooking.extensions?.length > 0 ? (
                <Table bordered hover striped>
                  <thead className="table-dark">
                    <tr><th>S.No</th><th>Type</th><th>Extended To</th><th>Hours</th><th>Amount</th><th>Transaction ID</th><th>Extended At</th></tr>
                  </thead>
                  <tbody>
                    {selectedBooking.extensions.map((ext, index) => (
                      <tr key={ext._id || index}>
                        <td>{index + 1}</td>
                        <td><Badge bg="success">Admin Car</Badge></td>
                        <td>{ext.extendDeliveryDate} {ext.extendDeliveryTime}</td>
                        <td>{ext.hours || 0}</td>
                        <td>₹{ext.amount || 0}</td>
                        <td>{ext.transactionId ? <code>{ext.transactionId}</code> : '-'}</td>
                        <td>{ext.extendedAt ? new Date(ext.extendedAt).toLocaleString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="alert alert-secondary">No extensions found.</div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowExtensionsModal(false)}>Close</Button></Modal.Footer>
      </Modal>

      {/* Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="xl" scrollable>
        <Modal.Header closeButton><Modal.Title>Booking Details</Modal.Title></Modal.Header>
        <Modal.Body>
          {bookingDetails && (
            <>
              <div className="row mb-4">
                <div className="col-md-6">
                  <h5 className="text-primary border-bottom pb-2">User Information</h5>
                  <p><strong>Name:</strong> {bookingDetails.userId?.name || 'N/A'}</p>
                  <p><strong>Email:</strong> {bookingDetails.userId?.email || 'N/A'}</p>
                  <p><strong>Mobile:</strong> {bookingDetails.userId?.mobile || 'N/A'}</p>
                </div>
                <div className="col-md-6">
                  <h5 className="text-primary border-bottom pb-2">Booking Information</h5>
                  <p><strong>Booking ID:</strong> <code>{bookingDetails._id}</code></p>
                  <p><strong>Status:</strong> <Badge bg={getStatusBadge(bookingDetails.status)} className="ms-2">{bookingDetails.status}</Badge></p>
                  <p><strong>Payment Status:</strong> <Badge bg={getPaymentBadge(bookingDetails.paymentStatus)} className="ms-2">{bookingDetails.paymentStatus}</Badge></p>
                  <p><strong>Total Price:</strong> ₹{bookingDetails.totalPrice}</p>
                </div>
              </div>
              <div className="row mb-4">
                <div className="col-md-6">
                  <h5 className="text-primary border-bottom pb-2">Rental Details</h5>
                  <p><strong>Rental Start:</strong> {bookingDetails.rentalStartDate ? new Date(bookingDetails.rentalStartDate).toLocaleDateString() : 'N/A'}</p>
                  <p><strong>Rental End:</strong> {bookingDetails.rentalEndDate ? new Date(bookingDetails.rentalEndDate).toLocaleDateString() : 'N/A'}</p>
                  <p><strong>Timings:</strong> {bookingDetails.from} - {bookingDetails.to}</p>
                  <p><strong>Pickup Location:</strong> {bookingDetails.pickupLocation || 'N/A'}</p>
                  <p><strong>Deposit:</strong> ₹{bookingDetails.deposit || 0}</p>
                  <p><strong>OTP:</strong> <code>{bookingDetails.otp || 'N/A'}</code></p>
                </div>
                <div className="col-md-6">
                  <h5 className="text-primary border-bottom pb-2">Car Information</h5>
                  <p><strong>Name:</strong> {bookingDetails.car?.carName}</p>
                  <p><strong>Model:</strong> {bookingDetails.car?.model}</p>
                  <p><strong>Vehicle Number:</strong> {bookingDetails.car?.vehicleNumber}</p>
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowDetailsModal(false)}>Close</Button></Modal.Footer>
      </Modal>
    </div>
  );
};

export default Bookings;